import { useState, useCallback, useEffect, useRef } from 'react'
import { encryptAndStore, decryptFromStore, removeFromStore } from '../utils/secureStorage'

/**
 * useBiometricAuth — autoryzacja dwuwarstwowa:
 *
 * Warstwa 1 (trwały dostęp): device_token
 *   - Generowany przez backend przy pierwszej aktywacji z linku zaproszeniowego
 *   - Przechowywany zaszyfrowany w localStorage (AES-GCM)
 *   - Ważny 30 dni (sliding window — odnawiany przy każdym użyciu)
 *   - Działa na KAŻDYM urządzeniu (telefon, desktop, tablet)
 *
 * Warstwa 2 (opcjonalna, telefon): WebAuthn credential
 *   - Rejestrowany przy aktywacji jeśli urządzenie wspiera WebAuthn
 *   - Jeśli jest → wymaga biometrii/PIN przy każdym wejściu (bezpieczeństwo)
 *   - Jeśli nie ma (desktop/brak zgody) → device_token używany bezpośrednio (cicho)
 *
 * Stany:
 *   loading      → inicjalizacja (spinner)
 *   authenticated → aplikacja dostępna
 *   locked        → mamy device_token, wymagana weryfikacja WebAuthn
 *   unauthorized  → brak device_token → wymagany link zaproszeniowy
 */

const LS_KEY_CONFIG     = 'pwa_config'       // zaszyfrowany: { deviceToken, jwt, email, firstName, lastName }
const LS_KEY_CREDENTIAL = 'pwa_credential_id' // base64 rawId WebAuthn
const LS_KEY_JWT        = 'pwa_jwt'           // ZASZYFROWANY JWT (AES-GCM)

// Dla brancha "prezentacja" zawsze traktujemy apkę jako demo — bez blokady po resume
const DEMO_BYPASS = true

function randomBuffer(len: number): ArrayBuffer {
  const buf = new Uint8Array(len)
  crypto.getRandomValues(buf)
  return buf.buffer as ArrayBuffer
}

function bufferToBase64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
}

function base64ToBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer as ArrayBuffer
}

function hasWebAuthn(): boolean {
  return !!window.PublicKeyCredential
}

function hasCredential(): boolean {
  return !!localStorage.getItem(LS_KEY_CREDENTIAL)
}

export type AuthState = 'loading' | 'unauthorized' | 'locked' | 'authenticated'

export function useBiometricAuth() {
  const [authState, setAuthState] = useState<AuthState>('loading')
  const [authReason, setAuthReason] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const attemptingRef = useRef(false)
  const onLockWipeRef = useRef<(() => void) | null>(null)

  const setOnLockWipe = useCallback((fn: () => void) => { onLockWipeRef.current = fn }, [])

  // ── Pobierz device_token z zaszyfrowanego localStorage ──────────────────────
  const getStoredDeviceToken = useCallback(async (): Promise<string | null> => {
    const cfg = await decryptFromStore(LS_KEY_CONFIG)
    if (!cfg) return null
    try {
      const parsed = JSON.parse(cfg)
      return parsed.deviceToken || null
    } catch { return null }
  }, [])

  // ── Zaloguj przez device_token (cichy, bez okienek) ─────────────────────────
  const loginWithDeviceToken = useCallback(async (): Promise<boolean> => {
    const deviceToken = await getStoredDeviceToken()
    if (!deviceToken) return false

    try {
      const r = await fetch('/api/auth/device-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceToken }),
      })
      if (!r.ok) {
        // Parsuj reason z 403 (organizacja/konto/licencja)
        if (r.status === 403) {
          try {
            const errData = await r.json()
            if (errData.reason) setAuthReason(errData.reason)
          } catch { /* ignore */ }
          setAuthState('unauthorized')
        }
        // device_token wygasł lub unieważniony — wyczyść
        removeFromStore(LS_KEY_CONFIG)
        localStorage.removeItem(LS_KEY_JWT)
        localStorage.removeItem(LS_KEY_CREDENTIAL)
        return false
      }
      const data = await r.json()
      const jwt = data.token
      // Zapisz odnowiony device_token
      const cfg = await decryptFromStore(LS_KEY_CONFIG)
      let existing: Record<string, unknown> = {}
      try { existing = cfg ? JSON.parse(cfg) : {} } catch { existing = {} }
      await encryptAndStore(LS_KEY_CONFIG, JSON.stringify({ ...existing, deviceToken: data.deviceToken, jwt }))
      await encryptAndStore(LS_KEY_JWT, jwt)
      setToken(jwt)
      setAuthState('authenticated')
      return true
    } catch {
      return false
    }
  }, [getStoredDeviceToken])

  // ── Rejestracja WebAuthn credential ─────────────────────────────────────────
  const registerCredential = useCallback(async (userInfo?: { name: string; displayName: string }): Promise<boolean> => {
    if (!hasWebAuthn()) return false
    try {
      const credential = await navigator.credentials.create({
        publicKey: {
          rp: { name: 'BlackApp', id: window.location.hostname },
          user: {
            id: randomBuffer(32),
            name: userInfo?.name || 'user@blackapp',
            displayName: userInfo?.displayName || 'Użytkownik BlackApp',
          },
          challenge: randomBuffer(32),
          pubKeyCredParams: [
            { type: 'public-key', alg: -7 },
            { type: 'public-key', alg: -257 },
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
            residentKey: 'preferred',
          },
          timeout: 60000,
          attestation: 'none',
        },
      }) as PublicKeyCredential | null

      if (credential) {
        localStorage.setItem(LS_KEY_CREDENTIAL, bufferToBase64(credential.rawId))
        return true
      }
      return false
    } catch (e) {
      console.warn('[biometric] registerCredential failed:', e)
      return false
    }
  }, [])

  // ── Weryfikacja WebAuthn (biometria/PIN) ─────────────────────────────────────
  const verifyWebAuthn = useCallback(async (): Promise<boolean> => {
    if (attemptingRef.current) return false
    if (!hasWebAuthn()) return false
    attemptingRef.current = true

    try {
      const credentialId = localStorage.getItem(LS_KEY_CREDENTIAL)
      const allowCredentials: PublicKeyCredentialDescriptor[] | undefined = credentialId
        ? [{ type: 'public-key', id: base64ToBuffer(credentialId) }]
        : undefined

      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: randomBuffer(32),
          timeout: 60000,
          userVerification: 'required',
          rpId: window.location.hostname,
          allowCredentials,
        },
      })
      attemptingRef.current = false
      return !!assertion
    } catch (e) {
      console.warn('[biometric] verifyWebAuthn failed:', e)
      attemptingRef.current = false
      return false
    }
  }, [])

  // ── Odblokuj (unlock) — WebAuthn → potem device_token jako fallback ──────────
  // Wywoływany przez InactivityLockScreen i ręczny przycisk "Odblokuj"
  const unlock = useCallback(async (): Promise<boolean> => {
    // 1. Próbuj WebAuthn
    if (hasWebAuthn() && hasCredential()) {
      const webAuthnOk = await verifyWebAuthn()
      if (webAuthnOk) {
        // WebAuthn OK → cicho odśwież JWT przez device_token
        const ok = await loginWithDeviceToken()
        if (ok) return true
        // device_token wygasł mimo WebAuthn OK — wyloguj
        setAuthState('unauthorized')
        return false
      }
      // WebAuthn nie powiodło się (anulowane, błąd) — zwróć false
      // InactivityLockScreen pokaże formularz tokenu
      return false
    }

    // 2. Brak WebAuthn lub brak credential → próbuj device_token bezpośrednio
    const ok = await loginWithDeviceToken()
    if (!ok) setAuthState('unauthorized')
    return ok
  }, [verifyWebAuthn, loginWithDeviceToken])

  // ── Odblokuj wpisanym tokenem zaproszeniowym (fallback ekranu blokady) ───────
  const unlockWithToken = useCallback(async (manualToken: string): Promise<boolean> => {
    try {
      const r = await fetch(`/api/auth/activate-app?token=${encodeURIComponent(manualToken)}`)
      if (!r.ok) {
        if (r.status === 403) {
          try {
            const errData = await r.json()
            if (errData.reason) setAuthReason(errData.reason)
          } catch { /* ignore */ }
        }
        return false
      }
      const data = await r.json()
      if (!data.userId || !data.deviceToken) return false

      const jwt = data.token
      await encryptAndStore(LS_KEY_CONFIG, JSON.stringify({
        deviceToken: data.deviceToken,
        jwt,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
      }))
      await encryptAndStore(LS_KEY_JWT, jwt)
      setToken(jwt)
      // Spróbuj zarejestrować WebAuthn na przyszłość (opcjonalne)
      const displayName = [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email || 'user@blackapp'
      await registerCredential({ name: data.email || 'user@blackapp', displayName })
      setAuthState('authenticated')
      return true
    } catch {
      return false
    }
  }, [registerCredential])

  // ── Lock — czyści RAM, przechodzi w stan blokady ────────────────────────────
  // DEMO BYPASS: podczas prezentacji nie blokuj aplikacji po bezczynności / resume
  const lock = useCallback(async () => {
    if (DEMO_BYPASS) {
      console.log('[DEMO] lock() called but ignored in demo/presentation mode')
      return
    }
    setToken(null)
    await removeFromStore(LS_KEY_JWT)
    onLockWipeRef.current?.()
    setAuthState('locked')
  }, [])

  // ── Inicjalizacja przy mount ─────────────────────────────────────────────────
  // DEMO / TEST BYPASS: aplikacja od razu authenticated, bez tokenu/linku
  const initialize = useCallback(async () => {
    console.log('[DEMO] Auth bypass active — app starts authenticated')
    setAuthState('authenticated')
    setToken(null)
  }, [])

  useEffect(() => {
    initialize()
  }, [initialize])

  return { authState, authReason, token, lock, unlock, unlockWithToken, setOnLockWipe }
}
